import { FC, lazy, useMemo, Suspense, useCallback, useEffect } from 'react';
import { Project } from '@brg/api';
import { Colors, FlexBox, useIsMobile, useScale, FixedFooterWrapper } from '@brg/ui';
import styled, { createGlobalStyle, useTheme } from 'styled-components';
import { useTranslation } from "react-i18next";
import { ApplicationShared } from "../../../../core";
import { getHasSpecialSourcesBlocks } from "../../../helpers/getHasSpecialSourcesBlocks";
import { ICoverContent } from 'shared/pages/exploreHseContextsNew/const/coverContent';
import { IVotesContent } from 'shared/pages/exploreHseContextsNew/const/votesContent';
import { LoaderSpinner } from "../../../modals/artzLegalsModal/LoaderAnimation";
import { AppTester } from "../../../../supporting";
import { HseContextActions } from "../../../reducers/hseCurrentContext";
import { useDispatch, useSelector } from "react-redux";
import { CurrentProjectSelector } from "../../../reducers/CurrentProject";
import {useGetPodcastLongreadProps} from "../../../components/AudioPlayer/hooks/useGetPodcastLongreadProps";
import { IAddProjectCoverContent } from 'shared/pages/projectEdit/components/PresentatitonBlock/types/AdditionalContentType';
import { getProjectHrefPrefix } from 'shared/pages/projectEdit/support/getProjectHrefPrefix';
import { navigationHandler } from 'shared/pages/projectEdit/support/navigationHandler';
import { executeLmsButtonPath } from 'shared/utils/executeLmsButtonPath';
import useQueryParams from 'shared/hooks/useQueryParams';

const LongRead = lazy(() => import('brg-longread').then(module => ({ default: module.LongRead })));

const LONGREAD_PAGING_LIMIT = 20;

type PropsT = {
  project: Project;
  onZoom?: (src: string) => void;
  onViewCurrentSlideScroll?: any;
  setMetaTags?: (val: string[]) => void;
  isTeamLG?: boolean;
  coverContent?: ICoverContent;
  votesContent?: IVotesContent;
  additionalContent?: IAddProjectCoverContent;
  disableVoting?: boolean;
  isThemeLongread?: boolean;
  customLGId?: string;
  themeIndex?: number;
  setPollTime?: (v: any, isFromVoteClick?: boolean) => void;
  onCheckHomework?: () => void;
  onSetThemeAnchors?: (e: any) => void;
  onApplyExam?: (e: any) => void;
  lastLongreadBlockLink?: string;
  courseGeneralColor?: string;
  teamId?: string;
  courseButtons: any[];
  lmsLongreadOrder?: string[];
  projectSource?: any;
  protectInfoComponent?: React.ReactNode;
  limitContentCount?: number;
  pagingLimit?: number;
  lmsModuleInfo?: any;
};

const LongreadWrapper = styled(FlexBox)`
  font-family: "HSE Sans";

  img {
    cursor: unset !important;
  }

  a {
    &[href^="/tag/"] {
      text-decoration: none !important;
    }
  }
`

const RegularFooterStyle = createGlobalStyle`
  body {
    ${FixedFooterWrapper} {
      //position: relative;
    }
  }
`

export const getLongreadSign = (project: any, lang: any) => {
  if (project?.isBilingual) {
    if (lang === 'en' && project?.longreadId_en) {
      return project?.longreadEnSign
    }
    if (lang === 'ru' && project?.longreadId) {
      return project?.longreadSign
    }
    return project?.longreadSign || project?.longreadEnSign;
  }
  return project?.longreadSign;
}
export const getLongreadId = (project: any, lang: any) => {
  if (project?.isBilingual) {
    if (lang === 'en' && project?.longreadId_en) {
      return project?.longreadId_en
    }
    if (lang === 'ru' && project?.longreadId) {
      return project?.longreadId
    }
    return project?.longreadId || project?.longreadId_en;
  }
  return project?.longreadId;
}

export const getTeamLgId = (team: any, lang: any) => {
  if (!!team?.isBilingual) {
    if (lang === 'en' && team?.longreadId_en) {
      return team?.longreadId_en
    }
    if (lang === 'ru' && team?.longreadId) {
      return team?.longreadId
    }
    return team?.longreadId || team?.longreadId_en;
  }

  return team?.longreadId
}


export const PSLongread: FC<PropsT> = ({
  project,
  onZoom,
  onViewCurrentSlideScroll,
  setMetaTags,
  isTeamLG,
  coverContent,
  votesContent,
  additionalContent,
  disableVoting,
  setPollTime,
  customLGId,
  projectSource,
  onCheckHomework,
  lastLongreadBlockLink,
  onSetThemeAnchors,
  courseGeneralColor,
  isThemeLongread,
  teamId,
  courseButtons,
  lmsLongreadOrder,
  themeIndex,
  onApplyExam,
  protectInfoComponent,
  limitContentCount,
  pagingLimit,
  lmsModuleInfo
}) => {
  const s = useScale();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const showOriginals = useSelector(CurrentProjectSelector)?.showOriginals;
  const podcastProps = useGetPodcastLongreadProps(project?.projectId)
  const { printType } = useQueryParams();

  const isLMSProject = useMemo(() => {
    return project?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item))
  }, [project])

  const teamModerationType = useMemo(() => {
    return project?.moderationType || null
  }, [project])

  const noGap = useMemo(() => {
    // @ts-ignore
    return project?.longread?.additionalInfo?.[0]?.rowGap === 0;
  }, [project])

  const moduleContentLink = useMemo(() => {
    // @ts-ignore
    return project?.additionalInfo?.contentLink;
  }, [project])
  
  const isDarkTheme = theme.projectView.titleColor === Colors.galBlack100;

  const longread = customLGId ? customLGId : isTeamLG ? getTeamLgId(project, i18n.language) : getLongreadId(project, i18n.language);
  const longreadSign = getLongreadSign(project, i18n.language);

  const viewType = !!customLGId ? projectSource?.longread?.viewType : (project?.longread?.viewType || 'container');
  const hasSpecialSourcesBlocks = useMemo(() => (
    getHasSpecialSourcesBlocks(project?.tagsID ? project?.tagsID?.map(tag => tag) : [])
  ), [project?.tagsID])

  const styles = useMemo(() => ({
    maxWidth: isMobile || ['fluid', 't-shape', 'fixed-ratio-large', 'free-type'].includes(String(viewType)) ? '100%' : s(1152) + 'px',
    width: '100%',
    margin: '0 auto',
  }), [isMobile, s, viewType]);

  const isWideLongread = ['fixed-ratio-large', 't-shape', 'free-type'].includes(String(viewType));

  const onSetAnchors = useCallback((anchors: any) => {
      //@ts-ignore
      const { visibleAnchorsLimit: limit } = project || {};
      if (anchors?.length) {
        dispatch(HseContextActions.setAnchorLinks({ anchors, limit: isLMSProject ? 3 : limit, override: isLMSProject }))
      }
  },[dispatch, isLMSProject])

  useEffect(() => {
    return () => {
      dispatch(HseContextActions.resetAnchorLinks())
    }
  }, [dispatch])

// @ts-ignore
const detailsPath = executeLmsButtonPath(project?.teams?.[0]?.buttons);

  
  return (
    <Suspense fallback={
      <FlexBox justify="center" align="center" width='100%' style={{ height: 200 }}>
        <LoaderSpinner color={theme.selectContextModal.loaderText} text=" "/>
      </FlexBox>
    }>
      <LongreadWrapper style={styles}>
        {isWideLongread && <RegularFooterStyle/>}
        <LongRead
          colorStyle={isDarkTheme ? 'light' : 'grey'}
          printType={printType}
          areaId=""
          isEditable={false}
          countryId=""
          type=""
          longreadId={longread}
          projectId={project?.projectId}
          author={project?.author}
          linkTo={'geography'}
          isLMS={isLMSProject}
          //@ts-ignore
          courseRoute={project?.teams?.[0]?.route} 
          //@ts-ignore
          contentLink={moduleContentLink} 
          teamModerationType={teamModerationType}
          detailsPath={detailsPath}
          themeIndex={themeIndex}
          courseButtons={courseButtons}
          //@ts-ignore
          lmsButtonLabel={project?.additionalInfo?.buttonLabel} 
          onOverlayedImageClick={(originalImage: any) => {
            onZoom?.(originalImage);
          }}
          viewType={viewType}
          theme={theme}
          appName={ApplicationShared.application}
          onViewCurrentSlideScroll={onViewCurrentSlideScroll}
          lang={i18n.language}
          disableVoting={disableVoting}
          setMetaTags={setMetaTags}
          hasSpecialSourcesBlocks={hasSpecialSourcesBlocks}
          includedCards={project?.includedCards || []}
          coverContent={{ ...coverContent }}
          votesContent={votesContent}
          protectInfoComponent={protectInfoComponent}
          limitContentCount={limitContentCount}
          additionalContent={additionalContent}
          //временный костыль для теста
          typeConnection={(process.env.REACT_APP_API_CONNECTION_MODE === 'develop' || process.env.REACT_APP_API_CONNECTION_MODE === 'staging') ? 'develop' : process.env.REACT_APP_API_CONNECTION_MODE}
          //@ts-ignore
          titleIsHidden={project?.longreadType ? projectSource?.isTitleHidden : project?.isTitleHidden}
          hseFonts={false}
          setUpdateVoteTime={setPollTime}
          onSetAnchors={onSetAnchors}
          showOriginals={showOriginals}
          onCheckHomework={onCheckHomework}
          lastLongreadBlockLink={lastLongreadBlockLink}
          courseGeneralColor={courseGeneralColor}
          onSetThemeAnchors={onSetThemeAnchors}
          sign={longreadSign}
          teamId={teamId}
          podcastProps={podcastProps}
          lmsLongreadOrder={lmsLongreadOrder}
          onApplyExam={onApplyExam}
          title={project?.title}
          noGap={noGap}
          pagingLimit={300}
          lmsModuleInfo={lmsModuleInfo}
          // pagingLimit={LONGREAD_PAGING_LIMIT}
          specBlockProps={{ getProjectHrefPrefix, navigationHandler }}
            //@ts-ignore
          titleNewPosition={project?.longreadType ? projectSource?.additionalInfo?.titlePosition : project?.additionalInfo?.titlePosition }
        />
      </LongreadWrapper>
    </Suspense>
  );
};
import { FC, lazy, useMemo, Suspense, useCallback, useEffect } from 'react';
import { Project } from '@brg/api';
import { Colors, FlexBox, useIsMobile, useScale, FixedFooterWrapper } from '@brg/ui';
import styled, { createGlobalStyle, useTheme } from 'styled-components';
import { useTranslation } from "react-i18next";
import { ApplicationShared } from "../../../../core";
import { getHasSpecialSourcesBlocks } from "../../../helpers/getHasSpecialSourcesBlocks";
import { ICoverContent } from 'shared/pages/exploreHseContextsNew/const/coverContent';
import { IVotesContent } from 'shared/pages/exploreHseContextsNew/const/votesContent';
import { LoaderSpinner } from "../../../modals/artzLegalsModal/LoaderAnimation";
import { AppTester } from "../../../../supporting";
import { HseContextActions } from "../../../reducers/hseCurrentContext";
import { useDispatch, useSelector } from "react-redux";
import { CurrentProjectSelector } from "../../../reducers/CurrentProject";
import {useGetPodcastLongreadProps} from "../../../components/AudioPlayer/hooks/useGetPodcastLongreadProps";
import { IAddProjectCoverContent } from 'shared/pages/projectEdit/components/PresentatitonBlock/types/AdditionalContentType';
import { getProjectHrefPrefix } from 'shared/pages/projectEdit/support/getProjectHrefPrefix';
import { navigationHandler } from 'shared/pages/projectEdit/support/navigationHandler';
import { executeLmsButtonPath } from 'shared/utils/executeLmsButtonPath';
import useQueryParams from 'shared/hooks/useQueryParams';

const LongRead = lazy(() => import('brg-longread').then(module => ({ default: module.LongRead })));

const LONGREAD_PAGING_LIMIT = 20;

type PropsT = {
  project: Project;
  onZoom?: (src: string) => void;
  onViewCurrentSlideScroll?: any;
  setMetaTags?: (val: string[]) => void;
  isTeamLG?: boolean;
  coverContent?: ICoverContent;
  votesContent?: IVotesContent;
  additionalContent?: IAddProjectCoverContent;
  disableVoting?: boolean;
  isThemeLongread?: boolean;
  customLGId?: string;
  themeIndex?: number;
  setPollTime?: (v: any, isFromVoteClick?: boolean) => void;
  onCheckHomework?: () => void;
  onSetThemeAnchors?: (e: any) => void;
  onApplyExam?: (e: any) => void;
  lastLongreadBlockLink?: string;
  courseGeneralColor?: string;
  teamId?: string;
  courseButtons: any[];
  lmsLongreadOrder?: string[];
  projectSource?: any;
  protectInfoComponent?: React.ReactNode;
  limitContentCount?: number;
  pagingLimit?: number;
  lmsModuleInfo?: any;
};

const LongreadWrapper = styled(FlexBox)`
  font-family: "HSE Sans";

  img {
    cursor: unset !important;
  }

  a {
    &[href^="/tag/"] {
      text-decoration: none !important;
    }
  }
`

const RegularFooterStyle = createGlobalStyle`
  body {
    ${FixedFooterWrapper} {
      //position: relative;
    }
  }
`

export const getLongreadSign = (project: any, lang: any) => {
  if (project?.isBilingual) {
    if (lang === 'en' && project?.longreadId_en) {
      return project?.longreadEnSign
    }
    if (lang === 'ru' && project?.longreadId) {
      return project?.longreadSign
    }
    return project?.longreadSign || project?.longreadEnSign;
  }
  return project?.longreadSign;
}
export const getLongreadId = (project: any, lang: any) => {
  if (project?.isBilingual) {
    if (lang === 'en' && project?.longreadId_en) {
      return project?.longreadId_en
    }
    if (lang === 'ru' && project?.longreadId) {
      return project?.longreadId
    }
    return project?.longreadId || project?.longreadId_en;
  }
  return project?.longreadId;
}

export const getTeamLgId = (team: any, lang: any) => {
  if (!!team?.isBilingual) {
    if (lang === 'en' && team?.longreadId_en) {
      return team?.longreadId_en
    }
    if (lang === 'ru' && team?.longreadId) {
      return team?.longreadId
    }
    return team?.longreadId || team?.longreadId_en;
  }

  return team?.longreadId
}


export const PSLongread: FC<PropsT> = ({
  project,
  onZoom,
  onViewCurrentSlideScroll,
  setMetaTags,
  isTeamLG,
  coverContent,
  votesContent,
  additionalContent,
  disableVoting,
  setPollTime,
  customLGId,
  projectSource,
  onCheckHomework,
  lastLongreadBlockLink,
  onSetThemeAnchors,
  courseGeneralColor,
  isThemeLongread,
  teamId,
  courseButtons,
  lmsLongreadOrder,
  themeIndex,
  onApplyExam,
  protectInfoComponent,
  limitContentCount,
  pagingLimit,
  lmsModuleInfo
}) => {
  const s = useScale();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const showOriginals = useSelector(CurrentProjectSelector)?.showOriginals;
  const podcastProps = useGetPodcastLongreadProps(project?.projectId)
  const { printType } = useQueryParams();

  const isLMSProject = useMemo(() => {
    return project?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item))
  }, [project])

  const teamModerationType = useMemo(() => {
    return project?.moderationType || null
  }, [project])

  const noGap = useMemo(() => {
    // @ts-ignore
    return project?.longread?.additionalInfo?.[0]?.rowGap === 0;
  }, [project])

  const moduleContentLink = useMemo(() => {
    // @ts-ignore
    return project?.additionalInfo?.contentLink;
  }, [project])
  
  const isDarkTheme = theme.projectView.titleColor === Colors.galBlack100;

  const longread = customLGId ? customLGId : isTeamLG ? getTeamLgId(project, i18n.language) : getLongreadId(project, i18n.language);
  const longreadSign = getLongreadSign(project, i18n.language);

  const viewType = !!customLGId ? projectSource?.longread?.viewType : (project?.longread?.viewType || 'container');
  const hasSpecialSourcesBlocks = useMemo(() => (
    getHasSpecialSourcesBlocks(project?.tagsID ? project?.tagsID?.map(tag => tag) : [])
  ), [project?.tagsID])

  const styles = useMemo(() => ({
    maxWidth: isMobile || ['fluid', 't-shape', 'fixed-ratio-large', 'free-type'].includes(String(viewType)) ? '100%' : s(1152) + 'px',
    width: '100%',
    margin: '0 auto',
  }), [isMobile, s, viewType]);

  const isWideLongread = ['fixed-ratio-large', 't-shape', 'free-type'].includes(String(viewType));

  const onSetAnchors = useCallback((anchors: any) => {
      //@ts-ignore
      const { visibleAnchorsLimit: limit } = project || {};
      if (anchors?.length) {
        dispatch(HseContextActions.setAnchorLinks({ anchors, limit: isLMSProject ? 3 : limit, override: isLMSProject }))
      }
  },[dispatch, isLMSProject])

  useEffect(() => {
    return () => {
      dispatch(HseContextActions.resetAnchorLinks())
    }
  }, [dispatch])

// @ts-ignore
const detailsPath = executeLmsButtonPath(project?.teams?.[0]?.buttons);

  
  return (
    <Suspense fallback={
      <FlexBox justify="center" align="center" width='100%' style={{ height: 200 }}>
        <LoaderSpinner color={theme.selectContextModal.loaderText} text=" "/>
      </FlexBox>
    }>
      <LongreadWrapper style={styles}>
        {isWideLongread && <RegularFooterStyle/>}
        <LongRead
          colorStyle={isDarkTheme ? 'light' : 'grey'}
          printType={printType}
          areaId=""
          isEditable={false}
          countryId=""
          type=""
          longreadId={longread}
          projectId={project?.projectId}
          author={project?.author}
          linkTo={'geography'}
          isLMS={isLMSProject}
          //@ts-ignore
          courseRoute={project?.teams?.[0]?.route} 
          //@ts-ignore
          contentLink={moduleContentLink} 
          teamModerationType={teamModerationType}
          detailsPath={detailsPath}
          themeIndex={themeIndex}
          courseButtons={courseButtons}
          //@ts-ignore
          lmsButtonLabel={project?.additionalInfo?.buttonLabel} 
          onOverlayedImageClick={(originalImage: any) => {
            onZoom?.(originalImage);
          }}
          viewType={viewType}
          theme={theme}
          appName={ApplicationShared.application}
          onViewCurrentSlideScroll={onViewCurrentSlideScroll}
          lang={i18n.language}
          disableVoting={disableVoting}
          setMetaTags={setMetaTags}
          hasSpecialSourcesBlocks={hasSpecialSourcesBlocks}
          includedCards={project?.includedCards || []}
          coverContent={{ ...coverContent }}
          votesContent={votesContent}
          protectInfoComponent={protectInfoComponent}
          limitContentCount={limitContentCount}
          additionalContent={additionalContent}
          //временный костыль для теста
          typeConnection={(process.env.REACT_APP_API_CONNECTION_MODE === 'develop' || process.env.REACT_APP_API_CONNECTION_MODE === 'staging') ? 'develop' : process.env.REACT_APP_API_CONNECTION_MODE}
          //@ts-ignore
          titleIsHidden={project?.longreadType ? projectSource?.isTitleHidden : project?.isTitleHidden}
          hseFonts={false}
          setUpdateVoteTime={setPollTime}
          onSetAnchors={onSetAnchors}
          showOriginals={showOriginals}
          onCheckHomework={onCheckHomework}
          lastLongreadBlockLink={lastLongreadBlockLink}
          courseGeneralColor={courseGeneralColor}
          onSetThemeAnchors={onSetThemeAnchors}
          sign={longreadSign}
          teamId={teamId}
          podcastProps={podcastProps}
          lmsLongreadOrder={lmsLongreadOrder}
          onApplyExam={onApplyExam}
          title={project?.title}
          noGap={noGap}
          pagingLimit={300}
          lmsModuleInfo={lmsModuleInfo}
          // pagingLimit={LONGREAD_PAGING_LIMIT}
          specBlockProps={{ getProjectHrefPrefix, navigationHandler }}
            //@ts-ignore
          titleNewPosition={project?.longreadType ? projectSource?.additionalInfo?.titlePosition : project?.additionalInfo?.titlePosition }
        />
      </LongreadWrapper>
    </Suspense>
  );
};
